import type { ReactNode } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { Badge } from '@cortex/web-design-system/components/ui/Badge';
import { Button } from '@cortex/web-design-system/components/ui/Button';
import {
  CUTTYSARK_VOTING_OPEN_PHASES,
  type CuttysarkLiveView,
} from '@cortex/domain-product-innovation-lab-types';
import { BallotForm, CandidateCard, ResultsView } from '../components/features/cuttysark/index.js';
import { canShowJoinMeet } from '../components/features/cuttysark/cuttysark-helpers.js';
import {
  useCuttysarkLive,
  useMyCuttysarkBallot,
  useMyCuttysarkComments,
} from '../hooks/useCuttysark.js';
import { ApiError } from '../lib/api/_request.js';

/**
 * @graph-node {Page}
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business CuttySark 賞セッションの参加者画面。進行状態を 2 秒ごとに読み直し、代表者紹介・発表中の人・採点と投票（紹介から締切まで受付）・集計待ち・めくられた結果・終了後の自分への応援コメントを状態に応じて出す。スマホでも使える
 * @graph-connects useCuttysarkLive [calls] 進行状態のポーリング
 * @graph-connects useMyCuttysarkBallot [calls] 前回の投票の取得
 * @graph-connects useMyCuttysarkComments [calls] 自分への応援コメントの取得
 * @graph-connects BallotForm [calls] 採点・投票フォーム
 * @graph-connects ResultsView [calls] 結果の表示
 */
export const Route = createFileRoute('/cuttysark/$sessionId/')({
  component: CuttysarkParticipantRoute,
});

/**
 * ページ単位のガード（canAccessPage）は掛けない。PDG 以外の兼務者も、登録されている回は
 * 開けるようにするため、見られるかどうかは API（canViewCuttysarkSession）に任せ、
 * 403 のときは API のメッセージをそのまま出す。メニューは PDG・admin にだけ出る。
 * @graph-connects CuttysarkParticipantPage [calls] ページ本体
 */
function CuttysarkParticipantRoute(): ReactNode {
  return <CuttysarkParticipantPage />;
}

/** @graph-connects none */
function CuttysarkParticipantPage(): ReactNode {
  const { t } = useTranslation();
  const { sessionId } = Route.useParams();
  const { data: view, error, isLoading } = useCuttysarkLive(sessionId);

  if (isLoading) {
    return (
      <p className="text-sm text-[var(--color-text-muted)]">{t('cuttysark.common.loading')}</p>
    );
  }
  if (view === undefined) {
    return (
      <p className="text-sm text-[var(--color-error)]">
        {error instanceof ApiError && error.status === 404
          ? t('cuttysark.common.notFound')
          : error instanceof ApiError && error.status === 403
            ? error.message
            : t('cuttysark.common.loadError')}
      </p>
    );
  }

  return (
    <div className="mx-auto max-w-4xl space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant="info">{t(`cuttysark.stage.${view.stage}`)}</Badge>
            <Badge>{t(`cuttysark.phase.${view.phase}`)}</Badge>
            <span className="text-xs text-[var(--color-text-muted)]">{view.quarter}</span>
          </div>
          <h1 className="mt-1 text-lg font-semibold text-[var(--color-text-primary)]">
            🏆 {view.title}
          </h1>
        </div>
        <div className="flex flex-wrap gap-2">
          {/* 予選を行う Meet へ 1 クリックで入れるようにする（運営が設定画面で登録した URL。終了した回では出さない） */}
          {canShowJoinMeet(view) && (
            <a href={view.meetUrl} target="_blank" rel="noopener noreferrer">
              <Button type="button" size="sm" variant="primary">
                {t('cuttysark.participant.joinMeet')}
              </Button>
            </a>
          )}
          {/* 運営が Meet で共有しているのと同じステージ画面を、手元でも大きく見られるよう別タブで開く */}
          <Link to="/cuttysark/$sessionId/stage" params={{ sessionId }} target="_blank">
            <Button type="button" size="sm" variant="secondary">
              {t('cuttysark.participant.openStage')}
            </Button>
          </Link>
          {view.viewer.isOperator && (
            <Link to="/cuttysark/$sessionId/operate" params={{ sessionId }}>
              <Button type="button" size="sm" variant="secondary">
                {t('cuttysark.participant.operate')}
              </Button>
            </Link>
          )}
          <Link to="/cuttysark">
            <Button type="button" size="sm" variant="ghost">
              {t('cuttysark.common.back')}
            </Button>
          </Link>
        </div>
      </div>
      <PhaseContent sessionId={sessionId} view={view} />
    </div>
  );
}

/** @graph-connects none */
interface PhaseContentProps {
  sessionId: string;
  view: CuttysarkLiveView;
}

/**
 * 進行状態ごとの中身。紹介・発表・投票受付中は投票フォームを同じ位置に置き続け、
 * 段階が進んでも書きかけの採点・コメントが消えないようにする（位置が変わると作り直される）。
 * @graph-connects none
 */
function PhaseContent({ sessionId, view }: PhaseContentProps): ReactNode {
  const { t } = useTranslation();
  if (CUTTYSARK_VOTING_OPEN_PHASES.has(view.phase)) {
    return (
      <div className="space-y-4">
        <OpenPhaseHeader view={view} />
        <Voting sessionId={sessionId} view={view} />
      </div>
    );
  }
  switch (view.phase) {
    case 'preparing':
      return (
        <>
          <p className="text-sm text-[var(--color-text-muted)]">
            {t('cuttysark.participant.preparing')}
          </p>
          <CandidateGrid view={view} />
        </>
      );
    case 'closed':
      return (
        <p className="text-base text-[var(--color-text-primary)]">
          {t('cuttysark.participant.closed')}
        </p>
      );
    case 'revealing':
      // 結果はステージ画面で札 → ドラムロール → 発表の順に出すので、手元で先に見えないようにする
      return (
        <div className="space-y-3">
          <p className="text-base text-[var(--color-text-primary)]">
            {t('cuttysark.participant.revealing')}
          </p>
          <Link to="/cuttysark/$sessionId/stage" params={{ sessionId }} target="_blank">
            <Button type="button" size="sm" variant="primary">
              {t('cuttysark.participant.openStage')}
            </Button>
          </Link>
        </div>
      );
    default:
      return (
        <div className="space-y-4">
          <MyComments sessionId={sessionId} />
          <ResultsView
            divisions={view.divisions}
            candidates={view.candidates}
            results={view.results}
          />
        </div>
      );
  }
}

/**
 * 投票を受け付けている段階の上半分。紹介中は候補者一覧、発表中は発表者を出す。
 * 投票できる人は下のフォームに候補者が並ぶので、投票受付中の一覧は観覧のみの人にだけ出す。
 * @graph-connects CandidateGrid [calls] 候補者一覧
 * @graph-connects Presenting [calls] 発表者の表示
 */
function OpenPhaseHeader({ view }: { view: CuttysarkLiveView }): ReactNode {
  if (view.phase === 'intro') return <CandidateGrid view={view} />;
  if (view.phase === 'presenting') return <Presenting view={view} />;
  return view.viewer.canVote ? null : <CandidateGrid view={view} />;
}

/**
 * 終了した回で、自分（候補者）に届いた応援コメントを書き手を伏せて出す。候補者でなければ何も出さない。
 * @graph-connects useMyCuttysarkComments [calls] 自分への応援コメントの取得
 */
function MyComments({ sessionId }: { sessionId: string }): ReactNode {
  const { t } = useTranslation();
  const { data } = useMyCuttysarkComments(sessionId);
  if (data?.available !== true) return null;
  return (
    <section className="rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-surface)] p-4">
      <h2 className="text-base font-semibold text-[var(--color-text-primary)]">
        {t('cuttysark.participant.myComments', { count: data.comments.length })}
      </h2>
      <p className="mt-1 text-xs text-[var(--color-text-muted)]">
        {t('cuttysark.participant.myCommentsHelp')}
      </p>
      {data.comments.length === 0 ? (
        <p className="mt-3 text-sm text-[var(--color-text-muted)]">
          {t('cuttysark.participant.myCommentsEmpty')}
        </p>
      ) : (
        <ul className="mt-3 space-y-2">
          {data.comments.map((comment, index) => (
            <li
              // 書き手を伏せたコメントで、同じ文面もあり得るので並び順を key に含める
              key={`${String(index)}-${comment}`}
              className="whitespace-pre-wrap rounded-md bg-[var(--color-bg-base)] p-3 text-sm text-[var(--color-text-primary)]"
            >
              {comment}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/**
 * 部門名を引く。
 * @graph-connects none
 */
function divisionNameOf(view: CuttysarkLiveView, divisionId: string): string {
  return view.divisions.find((d) => d.id === divisionId)?.name ?? divisionId;
}

/**
 * 候補者一覧（いま発表・紹介している人を強調）。
 * @graph-connects CandidateCard [calls] 候補者カード
 */
function CandidateGrid({ view }: { view: CuttysarkLiveView }): ReactNode {
  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
      {view.candidates.map((candidate) => (
        <CandidateCard
          key={candidate.email}
          candidate={candidate}
          divisionName={divisionNameOf(view, candidate.divisionId)}
          highlighted={candidate.email === view.currentCandidateEmail}
        />
      ))}
    </div>
  );
}

/**
 * 発表中の人を大きく出し、資料リンクを添える。
 * @graph-connects CandidateCard [calls] 候補者カード
 */
function Presenting({ view }: { view: CuttysarkLiveView }): ReactNode {
  const { t } = useTranslation();
  const current = view.candidates.find((c) => c.email === view.currentCandidateEmail);
  return (
    <div className="space-y-4">
      {current !== undefined && (
        <div>
          <p className="mb-1 text-xs text-[var(--color-text-muted)]">
            {t('cuttysark.participant.presenting')}
          </p>
          <CandidateCard
            candidate={current}
            divisionName={divisionNameOf(view, current.divisionId)}
            highlighted
          >
            {current.materialUrl !== '' && (
              <a
                href={current.materialUrl}
                target="_blank"
                rel="noreferrer"
                className="mt-2 inline-block text-sm text-[var(--color-accent-primary)] underline"
              >
                {t('cuttysark.participant.material')}
              </a>
            )}
          </CandidateCard>
        </div>
      )}
      <CandidateGrid view={view} />
    </div>
  );
}

/**
 * 投票フォーム（紹介・発表・投票受付中）。投票できる人には前回の投票を読み込んでからフォームを出す。
 * @graph-connects useMyCuttysarkBallot [calls] 前回の投票の取得
 * @graph-connects BallotForm [calls] 採点・投票フォーム
 */
function Voting({ sessionId, view }: PhaseContentProps): ReactNode {
  const { t } = useTranslation();
  const myBallot = useMyCuttysarkBallot(sessionId, view.viewer.canVote);

  if (!view.viewer.canVote) {
    return (
      <p className="text-sm text-[var(--color-text-muted)]">
        {t('cuttysark.participant.notVoter')}
      </p>
    );
  }
  if (myBallot.data === undefined) {
    return myBallot.isError ? (
      <p className="text-sm text-[var(--color-error)]">{myBallot.error.message}</p>
    ) : (
      <p className="text-sm text-[var(--color-text-muted)]">{t('cuttysark.common.loading')}</p>
    );
  }
  return (
    <div className="space-y-3">
      {view.viewer.hasSubmitted && (
        <p className="text-sm text-[var(--color-success)]">
          {t('cuttysark.participant.submitted')}
        </p>
      )}
      <BallotForm sessionId={sessionId} view={view} initialBallot={myBallot.data.ballot} />
    </div>
  );
}
